// src/utils/debugExperiments/store.ts
import * as Sentry from "@sentry/react"
import { EXPERIMENTS, ExperimentKey } from "./experiments"

const LS_KEY = "debug_experiments"

type State = Record<ExperimentKey, boolean>

const defaultState = (): State =>
  Object.fromEntries(
    Object.entries(EXPERIMENTS).map(([k, v]) => [k, v.default])
  ) as State

class DebugExperiments {
  private state: State = defaultState()
  private listeners = new Set<() => void>()
  private initialized = false

  /**
   * Reads persisted state from localStorage and applies all data-exp-* attributes
   * to <html>. Safe to call multiple times — only the first call has effect.
   * Must be called inside the `isDiagnosticsEnabled()` gate in `main.tsx`.
   */
  init(): void {
    if (this.initialized) return
    this.initialized = true

    try {
      const raw = localStorage.getItem(LS_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<State>
        this.state = { ...this.state, ...parsed }
      }
    } catch {
      // localStorage may be disabled (Telegram WebView) — fallback to defaults
    }

    this.applyAllToDom()
  }

  get<K extends ExperimentKey>(key: K): boolean {
    return this.state[key]
  }

  getAll(): Readonly<State> {
    return this.state
  }

  /** Returns list of enabled experiment keys, useful for tagging Sentry events. */
  getEnabledList(): ExperimentKey[] {
    return (Object.keys(this.state) as ExperimentKey[]).filter(
      (k) => this.state[k]
    )
  }

  set<K extends ExperimentKey>(key: K, value: boolean): void {
    if (this.state[key] === value) return

    this.state[key] = value
    this.persist()
    this.applyOneToDom(key)
    this.notify()

    Sentry.addBreadcrumb({
      category: "experiment",
      message: `${key}=${value}`,
      level: "info"
    })
  }

  /** Resets every experiment to its `default`. */
  resetAll(): void {
    const fresh = defaultState()
    let changed = false
    for (const key of Object.keys(EXPERIMENTS) as ExperimentKey[]) {
      if (this.state[key] !== fresh[key]) {
        this.state[key] = fresh[key]
        this.applyOneToDom(key)
        changed = true
      }
    }
    if (changed) {
      this.persist()
      this.notify()
      Sentry.addBreadcrumb({
        category: "experiment",
        message: "reset_all",
        level: "info"
      })
    }
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(this.state))
    } catch {
      // ignore — non-fatal
    }
  }

  private applyAllToDom(): void {
    for (const key of Object.keys(EXPERIMENTS) as ExperimentKey[]) {
      this.applyOneToDom(key)
    }
  }

  private applyOneToDom(key: ExperimentKey): void {
    const attr = `data-exp-${EXPERIMENTS[key].dataAttr}`
    document.documentElement.setAttribute(attr, this.state[key] ? "1" : "0")
  }

  private notify(): void {
    this.listeners.forEach((fn) => fn())
  }
}

export const debugExperiments = new DebugExperiments()
